import { forwardRef, memo, type Component, type ReactNode } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
// Material UI imports
import Box, { type BoxProps } from '@mui/material/Box';
// Skillmore UI Components
import { shortDateOptions } from '@empathco/ui-components/src/common/intl';
import { getJsDateFromISO } from '@empathco/ui-components/src/helpers/datetime';
import { getFullName } from '@empathco/ui-components/src/helpers/strings';
import BoxTypography from '@empathco/ui-components/src/mixins/BoxTypography';
// local imports
import { DevPlanOwner } from '../graphql/customTypes';

type DevPlanTimestampStatusProps = {
  readonly createdAt?: string | null;
  readonly updatedAt?: string | null;
  readonly owner?: DevPlanOwner | null;
  readonly myOwn?: boolean;
  readonly compact?: boolean;
  readonly tags?: ReactNode;
};

const DevPlanTimestampStatus = forwardRef<Component<BoxProps>, DevPlanTimestampStatusProps>(({
  createdAt,
  updatedAt,
  owner,
  myOwn = false,
  compact = false,
  tags
}, ref) => createdAt || updatedAt ? (
  <Box
      ref={ref}
      display="flex"
      flexWrap="wrap"
      alignSelf="flex-start"
      pt={compact ? 1 : 2}
      pb={0.5}
      px={compact ? { xs: 2, sm: 3.75 } : 2}
      columnGap={compact ? '3rem' : '4.5rem'}
  >
    {tags ? (
      <Box pb={0.5}>
        {tags}
      </Box>
    ) : undefined}
    {createdAt ? (
      <BoxTypography
          flexGrow={1}
          variant="caption"
          fontStyle="italic"
      >
        <FormattedMessage
            id={owner ? 'hr.dev_plan.created_by' : 'hr.dev_plan.created'}
            values={{
              // eslint-disable-next-line react/jsx-props-no-spreading
              date: <FormattedDate value={getJsDateFromISO(createdAt)} {...shortDateOptions}/>,
              ...(myOwn && { owner: null }) || (owner && { owner: getFullName(owner.first_name, owner.last_name) }) || {}
            }}
        />
      </BoxTypography>
    ) : undefined}
    {updatedAt ? (
      <BoxTypography
          flexGrow={1}
          variant="caption"
          fontStyle="italic"
      >
        <FormattedMessage
            id="hr.dev_plan.updated"
            values={{
              // eslint-disable-next-line react/jsx-props-no-spreading
              date: <FormattedDate value={getJsDateFromISO(updatedAt)} {...shortDateOptions}/>
            }}
        />
      </BoxTypography>
    ) : undefined}
  </Box>
) : null);

DevPlanTimestampStatus.displayName = 'DevPlanTimestampStatus';

export default memo(DevPlanTimestampStatus);
